import circleBg from '@/assets/notifications/new-market/medallion-circle-bg.svg';
import rocket from '@/assets/notifications/new-market/medallion-rocket.png';
import { POPUP_CTA } from '@/lib/notifications/popup-cta';
import { marketTitle, metaString } from '@/lib/notifications/meta';
import { Medallion } from '../Medallion';
import { PopupCta } from '../PopupCta';
import type { PopupViewProps } from '../types';

const CTA = POPUP_CTA['new-market'];

/**
 * NEW_MARKET — neutral/discovery. Purple rocket medallion, "New Market" title, a
 * "A new {category} market picked for you" subtitle, and the recommended market
 * question in larger bold curly-quoted type. CTA "Explore Market" deep-links to
 * the market.
 *
 * Data note: the NEW_MARKET producer only puts `marketName`/`externalMarketId`
 * in `meta` — the sub-category lives in `copy.vars.subCategoryName`, which the
 * response DTO doesn't expose. We read `subCategoryName`/`category` defensively
 * and gracefully drop the category word when it isn't present.
 */
export function NewMarketPopup({ notification, onAction }: PopupViewProps) {
  const meta = notification.meta;
  const question = marketTitle(meta);
  const category = metaString(meta, 'subCategoryName') ?? metaString(meta, 'category');
  const subtitle = category ? `A new ${category} market picked for you` : 'A new market picked for you';

  return (
    <>
      <Medallion circleSrc={circleBg} illustrationSrc={rocket} tone="neutral" illustrationSize={170} alt="" />

      <div className="flex flex-col items-center gap-2">
        <h2 className="text-3xl font-semibold tracking-[-0.6px] text-ink">New Market</h2>
        <p className="text-xl text-ink">{subtitle}</p>
      </div>

      {question && (
        <p className="text-[25px] font-bold leading-tight text-ink" data-testid="new-market-question">
          {`“${question}”`}
        </p>
      )}

      <PopupCta
        label={CTA.primary.label}
        width={320}
        onClick={() => onAction(CTA.primary.action)}
        testId="popup-cta-primary"
      />
    </>
  );
}
